/**
 * **The month under a lapse or an empty calendar.** A lapse looks back — three weeks and this one,
 * the last training ringed, and on the desk the same four weeks as attendance tiles; the outreach the canon drew as a message field is the desk card's
 * „Napisz DM". An empty calendar looks both ways, with the plan's un-booked days hatched ahead of today,
 * and offers the one scheduling write the backend has for it: shift what is left of the plan by a
 * week (`POST /plan-assignment/{id}/shift-remaining`).
 *
 * ── No booking from the card ─────────────────────────────────────────────────────────────────
 * The canon drew "Zaplanuj czw 24 · 18:00". There is no endpoint that books a plan day, and moving a
 * past booking is refused (`past_booking_stays_behind`), so the card offers no such write (owner,
 * 24 September 2026). The month shows what the trainer would book; the calendar is where it is done.
 */

import { useTranslation } from 'react-i18next';

import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';

import { useClientMonth } from '../lib/useClientMonth';
import { useClientWeek } from '../lib/useClientWeek';
import type { TaskAct } from '../lib/useTaskAct';
import type { TaskDensity, TaskSlots } from '../lib/taskWrites';
import type { AttentionItem } from '../model/attentionItems';
import { calendarDestination } from '../model/taskAction';
import { EMPTY_CALENDAR_FORTNIGHT, EMPTY_CALENDAR_MONTH, LAPSE_MONTH } from '../model/monthCalendar';
import { AttendanceTiles } from './AttendanceTiles';
import { BlockRead } from './BlockRead';
import { MonthCalendar } from './MonthCalendar';
import { ActButton, ActLink, ActRefusal, ActRow, ActSpacer } from './TaskActs';
import styles from './taskBlocks.module.css';

const SHIFT = 'shift';
const SHIFT_DAYS = 7;

interface MonthBlockProps {
  readonly item: AttentionItem;
  readonly density: TaskDensity;
  readonly todayIso: string;
  readonly slots: TaskSlots;
  readonly act: TaskAct;
}

export function MonthBlock({ item, density, todayIso, slots, act }: MonthBlockProps) {
  const { t } = useTranslation();
  const lapse = item.reason === 'lapsedActivity';
  const month = useClientMonth({
    customerId: item.subjectCustomerId,
    todayIso,
    span: lapse ? LAPSE_MONTH : density === 'quick' ? EMPTY_CALENDAR_FORTNIGHT : EMPTY_CALENDAR_MONTH,
    withPlan: !lapse,
  });
  // A lapse's four weeks as tiles under the month (owner, 26 September 2026). `LAPSE_MONTH` is the
  // same four Monday weeks `clientWeekSpan` asks for, so this is the month's own read, from cache.
  const attendance = density === 'desk' && lapse;
  const week = useClientWeek({ customerId: item.subjectCustomerId, wanted: attendance, todayIso, taskDay: null });
  const gate = useSkeletonGate(month.pending || week.pending);
  const plan = month.plan;
  const writes = density === 'short' ? undefined : slots.writes;
  const shifts = plan !== null && writes !== undefined;
  // The desk's way to the calendar itself, where the trainer books what the month shows (owner,
  // 26 September 2026). The Przegląd is the client's card already, one tab away.
  const calendar = density === 'desk' && item.subjectCustomerId !== null;

  return (
    <div className={`${String(styles.stack)} ${String(styles[density])}`} aria-busy={gate.waiting}>
      <BlockRead
        gate={gate}
        reserve={density === 'desk' ? styles.reserveMonthDesk : styles.reserveMonthQuick}
        failed={month.failed}
        note={month.month?.kind === 'undated' ? t('web:tasks.week.undated') : null}
      >
        {month.month === null || month.month.kind === 'undated' ? null : (
          <MonthCalendar
            month={month.month}
            ringLastTrained={lapse}
            named={density === 'desk'}
            note={
              density === 'desk' && !lapse && plan?.name != null
                ? t('web:tasks.blocks.month.planRuns', { plan: plan.name })
                : undefined
            }
          />
        )}
      </BlockRead>

      {!attendance || month.failed || week.failed || month.month?.kind === 'undated' ? null : (
        <AttendanceTiles weeks={gate.waiting || week.week?.kind !== 'strip' ? null : week.week.weeks} gate={gate} />
      )}

      {lapse || (!calendar && !shifts) ? null : (
        <>
          <ActRow label={t('web:tasks.blocks.month.shiftLabel')}>
            {/* Before the spacer, so the shift arriving with the plan's read moves nothing. */}
            {calendar && item.subjectCustomerId !== null ? (
              <ActLink
                label={t('web:tasks.blocks.month.openCalendar')}
                glyph="calendar"
                destination={calendarDestination(item.subjectCustomerId)}
              />
            ) : null}
            <ActSpacer />
            {plan === null || writes === undefined ? null : (
              <ActButton
                controller={act}
                tone="ink"
                glyph="shift"
                label={t('web:tasks.blocks.acts.shift')}
                // Once accepted the act is spent for this card (`useTaskAct` sends an act once), so it
                // says what it did rather than standing as a button that no longer does anything.
                run={{
                  act: SHIFT,
                  // NOT a close: a shift moves the not-started sessions, and whether any of them now
                  // lands ahead of today is the server's to say. The queue is invalidated, and the
                  // next read strikes the card if the item is gone (`reconcileDeck`).
                  closes: false,
                  done: t('web:tasks.blocks.live.shifted'),
                  write: () =>
                    writes.shiftRemaining(
                      { assignmentId: plan.id, clientId: item.subjectCustomerId },
                      { days: SHIFT_DAYS },
                    ),
                }}
              />
            )}
          </ActRow>
          <ActRefusal controller={act} acts={[SHIFT]} />
        </>
      )}
    </div>
  );
}
